import { useAtomValue, useSetAtom } from 'infrastructure/state/jotai.wrapper'
import {
  commonCoreFrameworkAtom,
  currentMappingSetAtom,
  newTreeAttributionAtom,
  treeCreateFlowIdAtom,
  treeStructureCreateDataAtom,
  treeStructureDataAtom,
  treeStructureFlowIdAtom,
} from './atoms'
import { useWithErrorNotification } from 'shared/ui/GlobalDialog'
import { createTree, createTreeStructure } from 'domains/scope-and-sequence/api'
import { useMemo } from 'react'

export function useCreateTreeStructure() {
  const theData = useAtomValue(treeStructureDataAtom)
  const doCreate = useWithErrorNotification(
    { errorTitle: 'Error Creating Tree Structure' },
    createTreeStructure,
  )
  const setFlowId = useSetAtom(treeStructureFlowIdAtom)

  return useMemo(() => {
    return {
      canCreate: !!theData,
      doCreate: async () => {
        if (!theData) return
        const result = await doCreate(
          theData.grade,
          theData.trees.map(({ treeId: tree_id, versionId: version_id }) => ({
            tree_id,
            version_id,
          })),
          theData.units,
        )
        setFlowId(result)
      },
    }
  }, [theData, setFlowId, doCreate])
}

export function useCreateScopeSequenceTree() {
  const data = useAtomValue(treeStructureCreateDataAtom)
  const commonCoreFramework = useAtomValue(commonCoreFrameworkAtom)
  const currentMappingSet = useAtomValue(currentMappingSetAtom)
  const attribution = useAtomValue(newTreeAttributionAtom)
  const doCreate = useWithErrorNotification(
    { errorTitle: 'Error Creating Tree' },
    createTree,
  )
  const setFlowId = useSetAtom(treeCreateFlowIdAtom)

  return useMemo(() => {
    const totalUnits = data?.units.length ?? 0
    const totalLessons =
      data?.units.reduce((acc, u) => acc + u.content.length, 0) ?? 0
    return {
      canCreate: !!data,
      doCreate: async () => {
        if (!data) return
        const result = await doCreate(
          data.grade,
          data.units,
          data.title,
          commonCoreFramework?.subject_id,
          attribution || undefined,
          currentMappingSet?.id,
        )
        setFlowId(result)
      },
      totalLessons,
      totalUnits,
    }
  }, [
    data,
    doCreate,
    setFlowId,
    commonCoreFramework,
    currentMappingSet,
    attribution,
  ])
}
